// Offline-capable wrapper for cloud transactions
// Queues mutations when offline and syncs when back online

import { useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './useUserProfile';
import { useNetworkStatus } from './useNetworkStatus';
import { useTranslation } from 'react-i18next';
import { queueMutation } from '@/lib/offlineQueue';
import { cacheData, getCachedData } from '@/lib/offlineQueue';
import { DbTransaction, DbCategory, TransactionTag } from './useTransactions';

const TRANSACTIONS_CACHE_KEY = 'cloud_transactions';
const CATEGORIES_CACHE_KEY = 'cloud_categories';

export function useOfflineTransactions() {
  const { user, isReady } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isOnline } = useNetworkStatus();

  // Deduplicate categories by name, type, and parent_id
  const deduplicateCategories = (cats: DbCategory[]): DbCategory[] => {
    const seen = new Map<string, DbCategory>();
    for (const cat of cats) {
      const key = `${cat.name}|${cat.type}|${cat.parent_id || 'null'}`;
      if (!seen.has(key)) {
        seen.set(key, cat);
      }
    }
    return Array.from(seen.values());
  };

  // Fetch categories with React Query + offline cache
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      // Try to fetch from server if online
      if (isOnline) {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .is('deleted_at', null)
          .order('name');

        if (error) {
          console.error('Error fetching categories:', error);
          // Fallback to cache
          const cached = await getCachedData<DbCategory[]>(CATEGORIES_CACHE_KEY);
          return cached ? deduplicateCategories(cached) : [];
        }

        // Deduplicate and cache the data
        const deduplicated = deduplicateCategories(data as DbCategory[]);
        await cacheData(CATEGORIES_CACHE_KEY, deduplicated);
        return deduplicated;
      } else {
        // Offline - use cache
        const cached = await getCachedData<DbCategory[]>(CATEGORIES_CACHE_KEY);
        return cached ? deduplicateCategories(cached) : [];
      }
    },
    enabled: !!user && isReady,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  // Fetch transactions with React Query + offline cache
  const { data: transactions = [], isLoading: transactionsLoading } = useQuery({
    queryKey: ['transactions', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      if (isOnline) {
        const { data, error } = await supabase
          .from('transactions')
          .select(`
            *,
            category:categories(*),
            transaction_tags(
              id,
              tag_id,
              tag:tags(id, name, color)
            )
          `)
          .order('date', { ascending: false })
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching transactions:', error);
          toast({
            title: t('common.error'),
            description: t('errors.loadingFailed'),
            variant: 'destructive',
          });
          // Fallback to cache
          const cached = await getCachedData<DbTransaction[]>(TRANSACTIONS_CACHE_KEY);
          return cached || [];
        }

        // Cache the data
        await cacheData(TRANSACTIONS_CACHE_KEY, data);
        return data as DbTransaction[];
      } else {
        // Offline - use cache
        const cached = await getCachedData<DbTransaction[]>(TRANSACTIONS_CACHE_KEY);
        return cached || [];
      }
    },
    enabled: !!user && isReady,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const loading = !isReady || categoriesLoading || transactionsLoading;

  // Real-time subscription (only when online)
  useEffect(() => {
    if (!user || !isOnline) return;

    const transactionsChannel = supabase
      .channel('transactions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['transactions', user.id] });
        }
      )
      .subscribe();

    const tagsChannel = supabase
      .channel('transaction-tags-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transaction_tags',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['transactions', user.id] });
        }
      )
      .subscribe();

    const categoriesChannel = supabase
      .channel('categories-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'categories',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['categories'] });
          queryClient.invalidateQueries({ queryKey: ['transactions'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(transactionsChannel);
      supabase.removeChannel(tagsChannel);
      supabase.removeChannel(categoriesChannel);
    };
  }, [user, queryClient, isOnline]);

  // Add transaction (with offline support)
  const addTransaction = useCallback(async (data: {
    type: 'income' | 'expense';
    amount: number;
    description?: string;
    category_id?: string;
    date?: string;
    tag_ids?: string[];
  }) => {
    if (!user) {
      toast({
        title: t('common.error'),
        description: t('auth.errors.generic'),
        variant: 'destructive',
      });
      return null;
    }

    const transactionId = crypto.randomUUID();
    const now = new Date().toISOString();
    
    const newTransaction = {
      id: transactionId,
      user_id: user.id,
      type: data.type,
      amount: data.amount,
      description: data.description || null,
      category_id: data.category_id || null,
      date: data.date || now,
      created_at: now,
      updated_at: now,
    };

    if (isOnline) {
      // Online - insert directly
      try {
        const { error } = await supabase
          .from('transactions')
          .insert(newTransaction);

        if (error) throw error;

        // Add tags
        if (data.tag_ids && data.tag_ids.length > 0) {
          const tagInserts = data.tag_ids.map(tag_id => ({
            transaction_id: transactionId,
            tag_id,
          }));
          await supabase.from('transaction_tags').insert(tagInserts);
        }

        queryClient.invalidateQueries({ queryKey: ['transactions', user.id] });
        
        toast({
          title: t('common.success'),
          description: t('transactions.transactionAdded'),
        });

        return newTransaction as unknown as DbTransaction;
      } catch (error) {
        console.error('Error adding transaction:', error);
        toast({
          title: t('common.error'),
          description: t('errors.errorOccurred'),
          variant: 'destructive',
        });
        return null;
      }
    } else {
      // Offline - queue for later
      await queueMutation('transaction', 'create', {
        ...newTransaction,
        tag_ids: data.tag_ids,
      });

      // Optimistically update cache
      const cached = await getCachedData<DbTransaction[]>(TRANSACTIONS_CACHE_KEY) || [];
      const category = categories.find(c => c.id === data.category_id);
      const optimisticTransaction: DbTransaction = {
        ...newTransaction,
        category: category || null,
        transaction_tags: [],
      };
      await cacheData(TRANSACTIONS_CACHE_KEY, [optimisticTransaction, ...cached]);
      
      // Trigger re-fetch from cache
      queryClient.invalidateQueries({ queryKey: ['transactions', user.id] });

      toast({
        title: t('common.success'),
        description: t('transactions.transactionAdded'),
      });

      return optimisticTransaction;
    }
  }, [user, isOnline, queryClient, toast, t, categories]);

  // Update transaction (with offline support)
  const updateTransaction = useCallback(async (
    id: string, 
    updateData: Partial<DbTransaction>, 
    tagIds?: string[]
  ) => {
    if (!user) return false;

    if (isOnline) {
      try {
        const { error } = await supabase
          .from('transactions')
          .update(updateData)
          .eq('id', id);

        if (error) throw error;

        // Update tags if provided
        if (tagIds !== undefined) {
          const { data: existingTags } = await supabase
            .from('transaction_tags')
            .select('id, tag_id')
            .eq('transaction_id', id);

          const existingTagIds = existingTags?.map(t => t.tag_id) || [];
          const tagsToRemove = existingTags?.filter(t => !tagIds.includes(t.tag_id)) || [];
          const tagsToAdd = tagIds.filter(tagId => !existingTagIds.includes(tagId));

          if (tagsToRemove.length > 0) {
            await supabase
              .from('transaction_tags')
              .delete()
              .in('id', tagsToRemove.map(t => t.id));
          }

          if (tagsToAdd.length > 0) {
            await supabase
              .from('transaction_tags')
              .insert(tagsToAdd.map(tag_id => ({ transaction_id: id, tag_id })));
          }
        }

        queryClient.invalidateQueries({ queryKey: ['transactions', user.id] });
        
        toast({
          title: t('common.success'),
          description: t('transactions.transactionUpdated'),
        });

        return true;
      } catch (error) {
        console.error('Error updating transaction:', error);
        toast({
          title: t('common.error'),
          description: t('errors.errorOccurred'),
          variant: 'destructive',
        });
        return false;
      }
    } else {
      // Offline - queue for later
      await queueMutation('transaction', 'update', {
        id,
        ...updateData,
        tag_ids: tagIds,
      });

      // Optimistically update cache
      const cached = await getCachedData<DbTransaction[]>(TRANSACTIONS_CACHE_KEY) || [];
      const updatedCache = cached.map(t => 
        t.id === id ? { ...t, ...updateData } : t
      );
      await cacheData(TRANSACTIONS_CACHE_KEY, updatedCache);
      
      queryClient.invalidateQueries({ queryKey: ['transactions', user.id] });

      toast({
        title: t('common.success'),
        description: t('transactions.transactionUpdated'),
      });

      return true;
    }
  }, [user, isOnline, queryClient, toast, t]);

  // Delete transaction (with offline support)
  const deleteTransaction = useCallback(async (id: string) => {
    if (!user) return false;

    if (isOnline) {
      try {
        const { error } = await supabase
          .from('transactions')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id);

        if (error) throw error;

        queryClient.invalidateQueries({ queryKey: ['transactions', user.id] });
        
        toast({
          title: t('common.success'),
          description: t('transactions.transactionDeleted'),
        });

        return true;
      } catch (error) {
        console.error('Error deleting transaction:', error);
        toast({
          title: t('common.error'),
          description: t('errors.errorOccurred'),
          variant: 'destructive',
        });
        return false;
      }
    } else {
      // Offline - queue for later
      await queueMutation('transaction', 'delete', { id });

      // Optimistically update cache
      const cached = await getCachedData<DbTransaction[]>(TRANSACTIONS_CACHE_KEY) || [];
      const updatedCache = cached.filter(t => t.id !== id);
      await cacheData(TRANSACTIONS_CACHE_KEY, updatedCache);
      
      queryClient.invalidateQueries({ queryKey: ['transactions', user.id] });

      toast({
        title: t('common.success'),
        description: t('transactions.transactionDeleted'),
      });

      return true;
    }
  }, [user, isOnline, queryClient, toast, t]);

  // Memoized calculations
  const { totalIncome, totalExpense, balance, expensesByCategory, incomesByCategory } = useMemo(() => {
    const totalIncome = transactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const totalExpense = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + Number(t.amount), 0);

    const balance = totalIncome - totalExpense;

    const expensesByCategory = transactions
      .filter(t => t.type === 'expense')
      .reduce((acc, t) => {
        const categoryId = t.category_id || 'uncategorized';
        if (!acc[categoryId]) {
          acc[categoryId] = {
            category: t.category,
            total: 0,
            transactions: [] as DbTransaction[],
          };
        }
        acc[categoryId].total += Number(t.amount);
        acc[categoryId].transactions.push(t);
        return acc;
      }, {} as Record<string, { category: DbCategory | null | undefined; total: number; transactions: DbTransaction[] }>);

    const incomesByCategory = transactions
      .filter(t => t.type === 'income')
      .reduce((acc, t) => {
        const categoryId = t.category_id || 'uncategorized';
        if (!acc[categoryId]) {
          acc[categoryId] = {
            category: t.category,
            total: 0,
            transactions: [] as DbTransaction[],
          };
        }
        acc[categoryId].total += Number(t.amount);
        acc[categoryId].transactions.push(t);
        return acc;
      }, {} as Record<string, { category: DbCategory | null | undefined; total: number; transactions: DbTransaction[] }>);

    return { totalIncome, totalExpense, balance, expensesByCategory, incomesByCategory };
  }, [transactions]);

  return {
    transactions,
    categories,
    loading,
    user,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    fetchTransactions: () => queryClient.invalidateQueries({ queryKey: ['transactions', user?.id] }),
    fetchCategories: () => queryClient.invalidateQueries({ queryKey: ['categories', user?.id] }),
    totalIncome,
    totalExpense,
    balance,
    expensesByCategory,
    incomesByCategory,
  };
}
